import { useState } from 'react';

import { Badge, OptionCard, RadioGroup } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { BindingPriceOut } from '@/lib/api/types';
import { formatPaise } from '@/lib/format';

export interface BindingPickerProps {
  bindings: BindingPriceOut[];
  /** Slug of the selected binding. */
  value: string | null;
  onChange: (slug: string) => void;
  /** Sheets in one finished book. The number the limits are measured against. */
  sheetsPerCopy: number;
  copies: number;
  /** True while a new set of prices is in flight; dims the numbers, keeps the layout. */
  refreshing?: boolean;
}

/**
 * How the finished book should be held together.
 *
 * Two decisions worth keeping:
 *
 * **A binding the shop cannot do is shown, disabled, with the reason.** Hiding
 * it would tell the customer we do not offer hard binding at all; showing
 * "Needs at least 20 sheets per copy" tells them what to change. Same reasoning
 * as the unstocked paper sizes in `PresetPicker`.
 *
 * **The card carries the added cost, not the order total.** This differs from
 * `PresetPicker` deliberately. There, the question is "which way of printing is
 * cheapest?" and four absolute totals answer it. Here the customer has already
 * settled the printing and is asking "what does binding cost me?" — so the
 * number that answers the question is the delta, already multiplied by copies.
 * The order total lives one card below in the breakdown and does not need
 * repeating on every row.
 *
 * **Each card carries a picture.** "Spiral binding" is a phrase that means
 * nothing to someone who has not held one, and the choice cannot be undone once
 * the job is printed. The image is `aria-hidden` and never the only signal: the
 * label, the description and the price all still say what the option is, so a
 * missing or slow image costs nothing.
 */
export function BindingPicker({
  bindings,
  value,
  onChange,
  sheetsPerCopy,
  copies,
  refreshing = false,
}: BindingPickerProps) {
  if (bindings.length === 0) return null;

  const dimmed = refreshing ? 'opacity-50 transition-opacity' : 'transition-opacity';

  return (
    <RadioGroup
      legend="How should it be bound?"
      hint={
        copies === 1
          ? `Your book is ${sheetsPerCopy} ${sheetsPerCopy === 1 ? 'sheet' : 'sheets'} thick.`
          : // Stated per copy because that is what the limits apply to, and
            // because it is the fact a customer needs to sanity-check the price.
            `Each of your ${copies} copies is ${sheetsPerCopy} ${
              sheetsPerCopy === 1 ? 'sheet' : 'sheets'
            } thick, and each is bound separately.`
      }
    >
      {bindings.map((entry) => {
        const { binding, is_available: available, binding_cost_paise: cost } = entry;
        const isFree = cost === 0;

        return (
          <OptionCard
            key={binding.id}
            name="binding"
            value={binding.slug}
            checked={value === binding.slug}
            disabled={!available}
            onChange={() => onChange(binding.slug)}
            media={<BindingThumbnail src={binding.image_url} dimmed={!available} />}
            label={
              <span className="flex flex-wrap items-center gap-2">
                {binding.label}
                {available && binding.lead_time_days > 0 ? (
                  // Worth a badge rather than fine print: it moves the delivery
                  // date, which is the thing a customer is actually planning
                  // around when they order a bound document.
                  <Badge tone="warning" srPrefix="This option">
                    +{binding.lead_time_days} {binding.lead_time_days === 1 ? 'day' : 'days'}
                  </Badge>
                ) : null}
              </span>
            }
            description={
              available ? (
                binding.description ? (
                  <>{binding.description}</>
                ) : null
              ) : (
                <>{entry.unavailable_reason}</>
              )
            }
            meta={
              available ? (
                <span className={dimmed}>
                  {isFree ? (
                    <span className="text-ink-muted">Included</span>
                  ) : (
                    <>
                      +{formatPaise(cost)}
                      {copies > 1 ? (
                        <span className="block text-xs font-normal text-ink-subtle">
                          {formatPaise(binding.price_paise)} each
                        </span>
                      ) : null}
                    </>
                  )}
                </span>
              ) : (
                <span className="text-ink-subtle">Unavailable</span>
              )
            }
          />
        );
      })}
    </RadioGroup>
  );
}

/**
 * A binding's picture, or a neutral stand-in.
 *
 * Fixed box so the rows do not jump as images arrive, `loading="lazy"` because
 * these are below the fold on a phone, and an `onError` fallback because a
 * catalogue URL is shop-editable and will eventually point at nothing. A broken
 * image icon in a checkout is worse than no image at all.
 */
function BindingThumbnail({ src, dimmed }: { src?: string | null; dimmed: boolean }) {
  const [failed, setFailed] = useState(false);
  const box = cn(
    'grid h-12 w-12 place-items-center overflow-hidden rounded-md',
    'border border-line bg-surface-sunken text-ink-muted',
    dimmed && 'opacity-40',
  );

  if (!src || failed) {
    // Not an empty box: the placeholder keeps the row rhythm identical whether
    // or not the shop has got round to photographing this binding.
    return (
      <span className={box}>
        <svg
          viewBox="0 0 24 24"
          className="h-6 w-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
        >
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 3v18" strokeLinecap="round" />
        </svg>
      </span>
    );
  }

  return (
    <span className={box}>
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
